import { faClose, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TextField } from '@mui/material';
import * as React from 'react';
import styled from 'styled-components';
import useTracking, { TrackingEventData } from '../../../hooks/useTracking';
import {
  BLACK2,
  BORDER_GRAY,
  PLACEHOLDER,
  SECONDARY_HOVER_GRAY,
  TEXT_DISABLED,
  WHITE,
} from '../../../theme';
import { EventNames } from '../../../utils/trackingEvents';
import { ButtonSize, ButtonVariant } from '../Button';
import { IconButton } from '../Button/IconButton';
import { TextColor, TextKind, textStyleFrom } from '../Text';

const Field = styled(TextField).attrs({
  variant: 'outlined',
})<{ $width?: string }>`
  background-color: ${WHITE};
  border-radius: 4px;
  color: ${PLACEHOLDER};
  align-self: flex-end;
  margin: 0;
  width: ${({ $width }) => $width || '300px'};

  & .MuiInputBase-adornedEnd {
    padding-right: 8px;
  }

  & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${BLACK2};
    border-width: 1px;
  }

  & .MuiOutlinedInput-notchedOutline {
    border-color: ${BORDER_GRAY};
  }
`;

const EndAdornmentContainer = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  gap: 4px;
`;

const EndAdornmentDivider = styled.div`
  display: block;
  width: 1px;
  height: 40px;
  background-color: ${BORDER_GRAY};
`;

const EndAdornmentIcon = styled(FontAwesomeIcon)<{ disabled?: boolean }>`
  width: 32px;
  height: 32px;
  line-height: 0;
  padding: 8px;
  border-radius: 50%;
  box-sizing: border-box !important;
  cursor: pointer;

  color: ${TEXT_DISABLED};
  background-color: transparent;

  &:hover {
    color: ${BLACK2};
    background-color: ${SECONDARY_HOVER_GRAY};
  }

  ${({ disabled }) =>
    disabled &&
    `
    cursor: default;

    &:hover {
      color: ${TEXT_DISABLED};
      background-color: transparent;
    }
  `}
`;

export const SearchField: React.FC<{
  hasClearButton?: boolean;
  defaultSearchInputValue?: string | null;
  onSearch: (searchValue: string) => void;
  onClear: () => void;
  onFocus?: () => void;
  width?: string;
  placeholder?: string;
  testId?: string;
  trackingData?: TrackingEventData;
}> = ({
  hasClearButton = true,
  defaultSearchInputValue,
  onSearch,
  onClear,
  onFocus,
  width,
  placeholder,
  testId = 'SearchMember',
  trackingData,
}) => {
  const { track } = useTracking();

  const [searchState, setSearchState] = React.useState(
    defaultSearchInputValue ?? '',
  );

  React.useEffect(() => {
    setSearchState(defaultSearchInputValue || '');
  }, [defaultSearchInputValue]);

  const handleChange = (event: React.BaseSyntheticEvent) => {
    setSearchState(event.target.value);
  };

  const handleSearch = () => {
    if (trackingData) {
      track(EventNames.SearchActivated, {
        ...trackingData,
      });
    }

    onSearch(searchState);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') {
      return;
    }

    handleSearch();
  };

  const handleCloseIconClick = () => {
    if (trackingData) {
      track(EventNames.SearchCleared, {
        ...trackingData,
      });
    }

    setSearchState('');
    onClear();
  };

  const handleFocus = () => {
    if (trackingData) {
      track(EventNames.SearchInitiated, {
        ...trackingData,
      });
    }
    onFocus?.();
  };

  return (
    <Field
      $width={width}
      data-testid={testId}
      placeholder={placeholder || 'Search members'}
      value={searchState}
      onChange={handleChange}
      onKeyDown={handleKeyPress}
      type="text"
      InputProps={{
        inputProps: {
          'data-testid': 'SearchInputField',
          'aria-label': placeholder || 'Search members',
        },
        style: {
          ...textStyleFrom(TextKind.BodyM, TextColor.Primary),
          height: 40,
          width: '100%',
        },
        endAdornment: (
          <EndAdornmentContainer>
            {hasClearButton && searchState && searchState !== '' && (
              <IconButton
                ariaLabel="Clear Search"
                onClick={handleCloseIconClick}
                testId="SearchInputField-CloseIcon"
                icon={<EndAdornmentIcon icon={faClose} />}
                size={ButtonSize.Small}
              />
            )}
            <EndAdornmentDivider />
            <IconButton
              ariaLabel="Search"
              variant={ButtonVariant.Transparent}
              onClick={handleSearch}
              disabled={!searchState}
              testId="SearchInputField-SearchIcon"
              icon={
                <EndAdornmentIcon disabled={!searchState} icon={faSearch} />
              }
              size={ButtonSize.Small}
            />
          </EndAdornmentContainer>
        ),
        onFocus: handleFocus,
      }}
    />
  );
};
